// Common Bangladesh medicine database for validation
export const MEDICINE_DATABASE = {
  // Paracetamol brands
  paracetamol: ['napa', 'ace', 'tylenol', 'panadol', 'fast', 'xpa'],
  
  // Omeprazole brands
  omeprazole: ['seclo', 'omepra', 'losectil', 'omez', 'pepnil'],
  
  // Esomeprazole brands
  esomeprazole: ['sergel', 'nexium', 'esomepra', 'esofag'],
  
  // Antibiotics
  azithromycin: ['zimax', 'azicin', 'azithro', 'zithromax'],
  cefixime: ['cef-3', 'cefixa', 'cefim', 'suprax'],
  amoxicillin: ['amoxil', 'novamox', 'acimox', 'amox'],
  
  // Blood pressure
  amlodipine: ['amlocard', 'amodis', 'amlodac', 'norvasc'],
  atenolol: ['tenolol', 'aten', 'tenormin'],
  losartan: ['losartil', 'cozaar', 'losacar'],
  
  // Diabetes
  metformin: ['comet', 'glucomin', 'glycomet', 'diabex'],
  glimepiride: ['amaryl', 'glimed', 'glimy'],
  
  // Cholesterol
  atorvastatin: ['atorva', 'lipitor', 'atorsave', 'stator'],
  
  // Antihistamines
  fexofenadine: ['fexo', 'allegra', 'fexova'],
  cetirizine: ['triz', 'cetrizin', 'zyrtec'],
  
  // Respiratory
  montelukast: ['montair', 'arokast', 'singulair'],
  
  // Pain/NSAID
  ibuprofen: ['brufen', 'ibufen', 'advil'],
  diclofenac: ['voltaren', 'diclo', 'difen'],
};

const UNKNOWN_GENERIC_VALUES = new Set([
  '',
  'not specified',
  'not provided',
  'unspecified',
  'unknown',
  'n/a',
  'na',
  'none',
  'null',
  'undefined',
  'needs verification',
  'need verification',
  'unverified',
  'verify',
  '-',
  '--',
]);

export function normalizeGenericName(generic?: string | null): string {
  return (generic || '').trim().replace(/\s+/g, ' ');
}

export function isKnownGenericName(generic?: string | null): boolean {
  const normalized = normalizeGenericName(generic).toLowerCase();
  return !UNKNOWN_GENERIC_VALUES.has(normalized);
}

export function canonicalGenericName(generic?: string | null): string {
  return normalizeGenericName(generic).toLowerCase();
}

export function validateMedicineName(name: string): {
  isValid: boolean;
  genericName?: string;
  confidence: number;
} {
  const normalized = name.toLowerCase().trim();
  
  // Check against database
  for (const [generic, brands] of Object.entries(MEDICINE_DATABASE)) {
    if (brands.some(brand => normalized.includes(brand) || brand.includes(normalized))) {
      return {
        isValid: true,
        genericName: generic.charAt(0).toUpperCase() + generic.slice(1),
        confidence: 0.95,
      };
    }
  }
  
  // Check if it's a generic name
  if (Object.keys(MEDICINE_DATABASE).includes(normalized)) {
    return {
      isValid: true,
      genericName: normalized.charAt(0).toUpperCase() + normalized.slice(1),
      confidence: 0.9,
    };
  }
  
  // Unknown medicine - might still be valid but less confident
  if (name.length >= 3 && /^[a-zA-Z][a-zA-Z0-9\s-]*$/i.test(name)) {
    return {
      isValid: true,
      confidence: 0.6, // Lower confidence for unknown medicines
    };
  }
  
  return { isValid: false, confidence: 0 };
}

export function validateDosage(dosage: string): boolean {
  if (!dosage) return true; // Optional field
  
  // Check for valid dosage patterns: 500mg, 20 mg, 5ml, etc.
  const dosagePattern = /^\d+(\.\d+)?\s*(mg|g|ml|mcg|iu|%)?$/i;
  return dosagePattern.test(dosage.trim());
}

export function validateFrequency(frequency: string): boolean {
  if (!frequency) return true; // Optional field
  
  // Common patterns: 1+0+1, 1-0-1, twice daily, TID, BID, once, etc.
  const patterns = [
    /^\d+[+\-]\d+[+\-]\d+$/, // 1+0+1
    /^(once|twice|thrice)/i,
    /^(tid|bid|qid|od)/i, // Medical abbreviations
    /^\d+\s*(times?|x)\s*(daily|per day)/i,
  ];
  
  return patterns.some(pattern => pattern.test(frequency.trim()));
}

export function enhanceGenericName(brandName: string, aiGeneric?: string): {
  genericName: string;
  genericConfirmed: boolean;
} {
  const validation = validateMedicineName(brandName);
  
  // Use validated generic name if available and confident
  if (validation.genericName && validation.confidence >= 0.9) {
    return {
      genericName: validation.genericName,
      genericConfirmed: true,
    };
  }
  
  const cleanedAiGeneric = normalizeGenericName(aiGeneric);
  if (isKnownGenericName(cleanedAiGeneric)) {
    return {
      genericName: cleanedAiGeneric,
      genericConfirmed: false,
    };
  }

  return {
    genericName: 'Needs verification',
    genericConfirmed: false,
  };
}
